import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Accordion,
  ChakraSimpleGrid,
  FAIconName,
  MenuOption,
  useToastMessage,
} from '@equip.health/ui';
import lodash from 'lodash';
import { Model, PageModel, Question } from 'survey-core';
import { Survey as SurveyJS } from 'survey-react-ui';

import 'survey-core/survey-core.min.css';
import './survey.css';

import { SurveyAccordionEvents } from '~/analytics/surveyAccordionEvents';
import ToastAccordion from '~/components/common/ToastAccordion';
import {
  ACCORDION_HEIGHT_CALCULATED,
  ACCORDION_WIDTH_LARGE,
} from '~/lib/constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { API_ERROR } from '~/lib/constants/error.constants';
import { SurveyResponseSource } from '~/lib/constants/survey/SurveyResponseSource';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useDebounce from '~/lib/hooks/useDebounce';
import { stringToJson } from '~/lib/util/survey.util';
import { useUpdateAssessmentMutation } from '~/store/services/assessments';

import CompactNavigation from './CompactNavigation';
import FullScreenContainer from './FullScreenContainer';
import SurveyTheme from '~/layouts/surveyJS/SurveyTheme';
import { AssessmentEventAttribute } from '~/analytics/assessmentEvents';
import { getAssessmentEventSource } from '~/lib/util/assessments.util';

const customSurveyStyles = {
  page: { root: 'survey-body', title: 'pageTitle' },
};

type SurveyAccordionProps = {
  data: SurveyResponse;
  isExpanded: boolean;
  isFullScreen: boolean;
  onDismiss: () => void;
  setIsExpanded: (isExpanded: boolean) => void;
  setIsFullScreen: (isFullScreen: boolean) => void;
};

const SurveyAccordion = ({
  data,
  isExpanded,
  isFullScreen,
  onDismiss,
  setIsExpanded,
  setIsFullScreen,
}: SurveyAccordionProps) => {
  const { currentUser } = useGlobalContext();
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  const {
    jsonResults,
    surveyDefinition,
    responseExternalId,
    status,
    surveyName,
    toUserEmail,
  } = data;

  const [currentPage, setCurrentPage] = useState<number>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [pages, setPages] = useState<PageModel[]>([]);
  const [doesIntendToClose, setDoesIntendToClose] = useState<boolean>(false);

  const [
    updateAssessment,
    {
      isSuccess: isUpdateAssessmentSuccess,
      isLoading: isUpdateAssessmentLoading,
      error: updateAssessmentError,
    },
  ] = useUpdateAssessmentMutation();

  const surveyRef = useRef<SurveyJS | null>(null);

  const pageMenuOptions: MenuOption[] = pages.map((page, i: number) => ({
    node: page.name,
    value: page.id ?? i.toString(),
  }));

  const { setComponentVisibility, surveyAccordion } =
    useComponentPortalContext();

  /**
   * Any time a survey is saved (including partial save),
   * update the status in the component portal
   */
  const refreshComponentVisibility = (status: SurveyResponseStatus): void => {
    if (surveyAccordion?.data?.status !== status) {
      setComponentVisibility({
        component: PortalComponent.SURVEY_ACCORDION,
        data: { ...data, status },
      });
    }
  };

  // Store the refreshSurveyInfo function in a ref so the current value
  // of its dependencies (data.status) is available when invoked by the
  // SurveyJS partial save callback
  const refreshComponentVisibilityRef = useRef(null);

  useEffect(() => {
    refreshComponentVisibilityRef.current = refreshComponentVisibility;
  }, [surveyAccordion?.data?.status]);

  const saveResults = (surveyModel: Model, ignoreStatus = false): void => {
    const newStatus =
      !ignoreStatus && currentPage === pages.length - 1
        ? SurveyResponseStatus.DONE
        : SurveyResponseStatus.IN_PROGRESS;

    const surveyModelData = {
      ...surveyModel.data,
      currentPage: surveyModel.currentPage.propertyHash.name,
      currentPageNo: surveyModel.currentPageNo,
    };

    updateAssessment({
      assessment: {
        jsonResults: JSON.stringify(surveyModelData),
        status: newStatus,
        submittedSource: SurveyResponseSource.WEB,
        submittedUserFirstName: currentUser.firstName,
        submittedUserId: currentUser.userId,
        submittedUserLastName: currentUser.lastName,
        toUserEmail,
        userTimezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      responseExternalId,
    });

    if (newStatus === SurveyResponseStatus.DONE) setIsSubmitting(true);
  };

  // Store the pages state in a ref so the current value
  // of its dependencies (handlePageChange) is available when invoked by the
  // SurveyJS onCurrentPageChanged callback
  const pagesRef = useRef(null);

  const handlePageChange = (surveyModel: Model, pageDetails) => {
    const newPage = pagesRef?.current?.find(
      (page: PageModel) => page.id === pageDetails.newCurrentPage.id,
    );

    setCurrentPage(newPage ? newPage.pageNum : surveyModel.currentPageNo);
  };
  const onSuccess = () => {
    refreshComponentVisibilityRef.current(SurveyResponseStatus.DONE);
  };

  const debounceSaveSurvey = useDebounce(1000 * 10);

  const autoSaveCallbackRef = useRef(null);

  const onValueChanged = (surveyModel: Model) => {
    setHasUnsavedChanges(true);
    debounceSaveSurvey(() => {
      if (autoSaveCallbackRef?.current)
        autoSaveCallbackRef.current(surveyModel);
    });
  };

  const survey = useMemo<Model>(() => {
    try {
      const surveyModel = new Model(JSON.parse(surveyDefinition));
      surveyModel.applyTheme(SurveyTheme);
      surveyModel.data = stringToJson(jsonResults);
      surveyModel.css = customSurveyStyles;
      surveyModel.onComplete.add(onSuccess);
      surveyModel.onCurrentPageChanged.add(handlePageChange);
      surveyModel.onPartialSend.add((originalSurvey: Model) => {
        const { currentPageNo, pages: _pages } = originalSurvey;
        const surveyToSave: Model = lodash.cloneDeep(originalSurvey);
        if (originalSurvey.currentPageNo < _pages.length - 1) {
          surveyToSave.currentPage = _pages[currentPageNo + 1].id;
          surveyToSave.currentPageNo = currentPageNo + 1;
        }
        saveResults(surveyToSave);
      });
      surveyModel.onValueChanged.add(onValueChanged);
      surveyModel.sendResultOnPageNext = true;
      surveyModel.showCompletedPage = false;
      surveyModel.showNavigationButtons = false;
      surveyModel.showTitle = false;
      surveyModel.textUpdateMode = 'onTyping';

      // mutating surveyModel is the only way to programmatically set the current page
      const json = stringToJson(jsonResults);
      const currentPageNo = json?.currentPageNo ?? 0;
      setCurrentPage(currentPageNo);
      surveyModel.clearInvisibleValues = 'onHidden';
      surveyModel.currentPage = json?.currentPage;
      surveyModel.currentPageNo = currentPageNo;
      surveyModel.showQuestionNumbers = true;

      setPages(surveyModel.pages);
      pagesRef.current = pages;
      return surveyModel;
    } catch (e) {
      console.error('Error parsing survey definition', e);
      createToast({
        id: 'load-survey-error',
        icon: FAIconName.exclamationTriangleRegular,
        text: 'We encountered an unexpected error loading this assessment. Please email support@equip.health',
        variant: 'critical',
      });
      return new Model();
    }
  }, [surveyDefinition]);

  useEffect(() => {
    autoSaveCallbackRef.current = (surveyModel: Model) => {
      if (!isUpdateAssessmentLoading && hasUnsavedChanges)
        saveResults(surveyModel, true);
    };
  }, [isUpdateAssessmentLoading, hasUnsavedChanges]);

  useEffect(() => {
    setComponentVisibility({
      component: PortalComponent.SURVEY_ACCORDION,
      hasUnsavedChanges,
    });
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (
      currentPage === pages.length - 1 &&
      isSubmitting &&
      isUpdateAssessmentSuccess
    ) {
      // Add toast with success message here
      survey?.completeLastPage();
    }
  }, [isSubmitting, isUpdateAssessmentSuccess]);

  const onClose = (): void => {
    setDoesIntendToClose(false);
    setHasUnsavedChanges(false);
    onDismiss();
  };

  useEffect(() => {
    if (isUpdateAssessmentSuccess) {
      if (doesIntendToClose) {
        onClose();
      } else {
        if (!isSubmitting) {
          refreshComponentVisibilityRef.current(
            SurveyResponseStatus.IN_PROGRESS,
          );
        }
        setHasUnsavedChanges(false);
      }
    }
  }, [isUpdateAssessmentSuccess]);

  const handlePreviousClick = (): void => {
    survey.prevPage();
    saveResults(survey, true);
  };

  const handleNextClick = (): void => {
    const eventParams = {
      [AssessmentEventAttribute.ResponseExternalId]: responseExternalId,
      [AssessmentEventAttribute.Source]: getAssessmentEventSource(
        window.location.pathname,
      ),
      [AssessmentEventAttribute.SurveyName]: surveyName,
    };
    trackEvent(SurveyAccordionEvents.surveyNextButtonClicked, eventParams);
    survey.nextPage();
  };

  const handleSkip = (pageId: string | null): void => {
    // pageId can be null
    if (pageId !== null) {
      const currentPageNo = pages.findIndex((page) => page.id === pageId);
      survey.currentPage = pageId;
      survey.currentPageNo = currentPageNo;

      const currentPageDetail = pages[currentPageNo];
      setCurrentPage(currentPageDetail.num - 1);

      saveResults(survey, true);
    }
  };

  const getFirstPageError = (): string => {
    // EQ-7365 : Iterate over each Question and check for errors, instead of page
    const questions: Question[] = survey.getAllQuestions(true); // true - visible questions
    for (const question of questions) {
      if (question.hasErrors()) {
        const questionParentPage = question.parent as PageModel;

        // Set the focus on the question
        survey.focusQuestion(question.name);

        // @ts-expect-error-next-line type is inaccurate, this is not how they want us to do it i believe but works for interim
        if (questionParentPage.propertyHash?.parent?.propertyHash) {
          // @ts-expect-error-next-line
          return questionParentPage.propertyHash.parent.propertyHash.name;
        }
        // @ts-expect-error-next-line
        return questionParentPage.propertyHash.name;
      }
    }
    return null;
  };

  const handleSubmit = () => {
    if (!survey.hasErrors()) {
      setIsSubmitting(true);
      const eventParams = {
        [AssessmentEventAttribute.ResponseExternalId]: responseExternalId,
        [AssessmentEventAttribute.Source]: getAssessmentEventSource(
          window.location.pathname,
        ),
        [AssessmentEventAttribute.SurveyName]: surveyName,
      };
      trackEvent(SurveyAccordionEvents.surveySubmitButtonClicked, eventParams);
      saveResults(survey);
    } else {
      handleSkip(getFirstPageError());
    }
  };

  const handleClose = (): void => {
    trackEvent(SurveyAccordionEvents.surveyAccordionCloseButtonClicked, {
      'Current Page': currentPage,
      'Has Unsaved Changes': hasUnsavedChanges,
      [AssessmentEventAttribute.ResponseExternalId]: responseExternalId,
      [AssessmentEventAttribute.SurveyName]: surveyName,
    });
    if (hasUnsavedChanges) {
      setDoesIntendToClose(true);
      saveResults(survey, true);
    } else {
      onClose();
    }
  };

  if (status === SurveyResponseStatus.DONE) {
    return (
      <ToastAccordion
        data={{ message: 'Survey submitted', title: `${surveyName} Survey` }}
        isExpanded={isExpanded}
        onDismiss={onDismiss}
        setIsExpanded={setIsExpanded}
      />
    );
  }

  let updateAssessmentErrorStr: string = '';
  if (updateAssessmentError) {
    if (updateAssessmentError && 'data' in updateAssessmentError) {
      updateAssessmentErrorStr = updateAssessmentError.data.toString();
    } else {
      updateAssessmentErrorStr = API_ERROR;
    }
  }

  const handleSetIsExpanded = (expanded: boolean) => {
    const eventName = expanded
      ? SurveyAccordionEvents.surveyAccordionMaximizeIconClicked
      : SurveyAccordionEvents.surveyAccordionMinimizeIconClicked;
    trackEvent(eventName, {
      'Current Page': currentPage,
      [AssessmentEventAttribute.ResponseExternalId]: responseExternalId,
      [AssessmentEventAttribute.SurveyName]: surveyName,
    });
    setIsExpanded(expanded);
  };

  const handleSetIsFullScreen = (fullScreen: boolean) => {
    const eventName = fullScreen
      ? SurveyAccordionEvents.surveyAccordionMaximizeIconClicked
      : SurveyAccordionEvents.surveyAccordionMinimizeIconClicked;
    trackEvent(eventName, {
      'Current Page': currentPage,
      'Is Full Screen': fullScreen,
      [AssessmentEventAttribute.ResponseExternalId]: responseExternalId,
      [AssessmentEventAttribute.SurveyName]: surveyName,
    });
    setIsFullScreen(fullScreen);
  };

  return !survey ? null : (
    <Accordion
      dismissMenu={handleClose}
      expandMax
      footer={
        <CompactNavigation
          currentPage={currentPage}
          error={updateAssessmentErrorStr}
          isFullScreen={isFullScreen}
          isLoading={isUpdateAssessmentLoading}
          onNext={handleNextClick}
          onPrevious={handlePreviousClick}
          onSkip={handleSkip}
          onSubmit={handleSubmit}
          pages={pageMenuOptions}
          responseExternalId={responseExternalId}
          surveyName={surveyName}
        />
      }
      id="survey"
      isExpanded={isExpanded}
      isFullScreen={isFullScreen}
      isFullWidth
      itemBorderTopWidth="0"
      lastItemBorderBottomWidth="0"
      maxHeight="unset"
      panelContentMaxHeight="none"
      panelMinWidth={ACCORDION_WIDTH_LARGE}
      progressMax={pageMenuOptions.length}
      progressMin={0}
      progressValue={currentPage}
      setIsExpanded={handleSetIsExpanded}
      setIsFullScreen={handleSetIsFullScreen}
      title={`${surveyName} Survey`}
    >
      {!isFullScreen ? (
        <ChakraSimpleGrid
          backgroundColor="white"
          columns={1}
          maxHeight={ACCORDION_HEIGHT_CALCULATED}
          overflow="auto"
          position="relative"
          spacing="16px"
          width="100%"
        >
          <SurveyJS model={survey} ref={surveyRef} />
        </ChakraSimpleGrid>
      ) : (
        <FullScreenContainer
          currentPage={currentPage}
          isLoading={isUpdateAssessmentLoading}
          onSkip={handleSkip}
          pages={pages}
          responseExternalId={responseExternalId}
        >
          <SurveyJS model={survey} ref={surveyRef} />
        </FullScreenContainer>
      )}
    </Accordion>
  );
};

export default SurveyAccordion;
